import axios, { AxiosError, AxiosInstance, CreateAxiosDefaults, InternalAxiosRequestConfig } from "axios";
import {IAuthError, IAuthResponse, IAuthResponseExtensions, ILoginCredentials} from "@/types/auth";
import { ISession } from "@/types/ISession";
import { IInstallmentDetail } from "@/types/IInstallment";
import {IGlobalWarning, IPaymentProvider, IPaymentProviderBackend, IProvidersResponse} from "@/types/IPaymentProvider";
import {
  IReference,
  IReferenceRequest,
  IReferenceResponse,
  IBitsoReferenceRequest,
  IBitsoReferenceResponse,
} from "@/types/IReferenceResponse";
import {
  ISuperPacmanFetchInstallmentRequest,
  ISuperPacmanInstallment,
  ISuperPacmanReferenceRequest,
  ISuperPacmanReferenceResponse,
  ISuperPacmanVirtualAccountNumberRequest,
  ISuperPacmanVirtualAccountNumberResponse,
  IXenditReferenceRequest,
  IXenditReferenceResponse,
} from "@/types/ISuperPacman";
import { formatDate } from "@/shared-helpers/date-time.helper";
import { IActionLogsRequest, IActionLogsResponse } from "@/types/IActionLogs";
import { VueGtag } from "vue-gtag-next";
import { logGTagEvent } from "@/helpers/google-analytics.helpers";
import { Providers } from "@/types/Providers";
import type { IWompiAcceptanceTokenResponse, IWompiTransactionResponse } from "@/types/IWompiResponse";
import { snakeToCamel } from "@/shared-helpers/snake-to-camel.helper";
import {
  IAvailableExtensionsResponse, IExtensionOption,
  IExtensionOptionBackend,
  IInitiateExtensionRequest,
  IInitiateExtensionResponse, ISelectExtensionRequest, ISelectExtensionResponse
} from "@/types/IExtensions";
import {
  IRolloutDeviceAssignmentRequest,
  IRolloutDeviceAssignmentResponse,
} from "@/types/IRollout";
import i18n from "@/i18n";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const EXTENSIONS_ACCESS_TOKEN_KEY = "extensions_access_token";
const EXTENSIONS_DEVICE_TAG_KEY = "extensions_device_tag";
const SESSION_KEY = "session_data";
const REDIRECT_ROUTE_PREFIX_KEY = "redirect_route_prefix";
const DEVICE_TAG_KEY = "device_tag";
const SHARED_PASSWORD = process.env.VUE_APP_MIDDLEWARE_SHARED_SECRET || "";

const BASE_URL: string = process.env.VUE_APP_MIDDLEWARE_URL || "";
const LOGIN_URL: string = process.env.VUE_APP_MIDDLEWARE_AUTH || "";
const REFRESH_URL: string = process.env.VUE_APP_MIDDLEWARE_REFRESH || "";
const PROVIDERS_URL: string = process.env.VUE_APP_MIDDLEWARE_PROVIDERS || "";
const REFERENCE_URL: string = process.env.VUE_APP_MIDDLEWARE_REFERENCE || "";
const LOG_URL: string = process.env.VUE_APP_MIDDLEWARE_LOGS_URL || "";
const ROLLOUT_DEVICE_ASSIGNMENT_URL = "rollouts/device-assignment/";
const EXTENSIONS_SERVICE_BASE_URL: string = process.env.VUE_APP_EXTENSIONS_SERVICE_BASE_URL || "";
const UI_FLAGS_URL: string = process.env.VUE_APP_MIDDLEWARE_UI_FLAGS || "ui-flags/";

class AuthHelper {
  private api: AxiosInstance;
  private extensionsApi: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
      timeout: 10000,
    } as CreateAxiosDefaults) as AxiosInstance;

    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Proactively refresh when < 10% of token lifetime remains
        if (this.isTokenApproachingExpiry()) {
          await this.proactiveRefresh();
        }

        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshToken = this.getRefreshToken();
          if (!refreshToken) {
            await this.logoutAndRedirect();
            throw new IAuthError("No refresh token available", 401);
          }

          try {
            const response = await axios.post<IAuthResponse>(
              `${BASE_URL}${REFRESH_URL}`,
              { refresh: refreshToken }, // Updated to match API expectation
            );

            const { access, refresh } = response.data;

            this.setToken(access);
            this.setRefreshToken(refresh);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${access}`;
            }
            return this.api(originalRequest);
          } catch (refreshError) {
            await this.logoutAndRedirect();
            throw new IAuthError("Failed to refresh token", (refreshError as AxiosError).response?.status);
          }
        } else if (
          error.response?.status === 400 &&
          error.response?.data &&
          typeof error.response.data === "object" &&
          error.response.data["error_details"]
        ) {
          // We've received details about the declined transaction
          return Promise.resolve(error.response);
        }
        throw new IAuthError(error.message, error.response?.status, error.response?.data);
      },
    );

    // Extensions Service Manager API interceptor
    this.extensionsApi = axios.create({
      baseURL: EXTENSIONS_SERVICE_BASE_URL,
      timeout: 10000,
    } as CreateAxiosDefaults) as AxiosInstance;

    this.extensionsApi.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getExtensionsToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["Accept-Language"] = i18n.global.locale.value;
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );

    this.extensionsApi.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const extensionsDeviceTag = this.getExtensionsDeviceTag();
            const response = await axios.post<IAuthResponseExtensions>(
              `${EXTENSIONS_SERVICE_BASE_URL}app-www-token/`,
              { deviceTag: extensionsDeviceTag, sub: `${extensionsDeviceTag}@app.www`, channel: "APP-WWW" },
            );

            const { token, channel, expiresAt, expiresIn, sub } = response.data;
            this.setExtensionsToken(token);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return this.extensionsApi(originalRequest);
          } catch (refreshError) {
            this.logoutExtensionsAndRedirect();
            throw new IAuthError("Failed to refresh extensions token", (refreshError as AxiosError).response?.status);
          }
        }
        throw new IAuthError(error.message, error.response?.status, error.response?.data);
      },
    );
  }

  /**
   * Decodes the stored access token and returns its `iat` and `exp` claims.
   *
   * Returns `null` when no token is stored or the payload cannot be decoded
   * (malformed token, missing claims). Callers should treat `null` as
   * "expiry unknown" and skip any proactive refresh.
   */
  private getTokenClaims(): { iat: number; exp: number } | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      // JWTs use base64url encoding; convert to standard base64 before decoding.
      const base64url = token.split(".")[1];
      const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      if (typeof payload.iat === "number" && typeof payload.exp === "number") {
        return { iat: payload.iat, exp: payload.exp };
      }
    } catch {
      // Malformed token — treat as non-expiring to avoid blocking requests.
    }
    return null;
  }

  /**
   * Returns `true` when less than 10% of the access token's lifetime remains
   * and the token has not yet expired.
   *
   * Already-expired tokens return `false` so they fall through to the reactive
   * 401 handler instead of paying for a proactive refresh whose only benefit —
   * avoiding a failed request — no longer applies (the request has already
   * expired). Returns `false` when claims cannot be decoded.
   */
  private isTokenApproachingExpiry(): boolean {
    const claims = this.getTokenClaims();
    if (!claims) return false;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const lifetime = claims.exp - claims.iat;
    const remaining = claims.exp - nowSeconds;

    // Only proactive-refresh tokens that are still valid: if the token is
    // already expired, the next request will 401 and the reactive handler
    // will refresh anyway — a proactive call here would just add latency.
    return lifetime > 0 && remaining > 0 && remaining / lifetime < 0.1;
  }

  /**
   * Silently refreshes the access token before it expires, keeping the session
   * alive without the user ever seeing a failed request.
   *
   * Called by the request interceptor when `isTokenApproachingExpiry` is true.
   * Failures are swallowed — if the refresh endpoint rejects the request, the
   * reactive 401 handler will attempt another refresh, and `logoutAndRedirect`
   * is the final fallback.
   *
   * Concurrent calls are deduplicated: all callers await the same in-flight
   * promise so only one refresh request is ever sent at a time.
   *
   * The hierarchy is:
   *  1. Proactive refresh → session stays alive invisibly
   *  2. Reactive refresh (401 handler) → recovers if proactive missed
   *  3. logoutAndRedirect → last resort when both access and refresh tokens are dead
   */
  private async proactiveRefresh(): Promise<void> {
    // Deduplicate concurrent refresh attempts.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return;

        const response = await axios.post<IAuthResponse>(`${BASE_URL}${REFRESH_URL}`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        this.setToken(access);
        this.setRefreshToken(refresh);
      } catch {
        // Proactive refresh failed silently; the reactive 401 handler will
        // attempt again if the server rejects the stale token.
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async login(credentials: ILoginCredentials): Promise<IAuthResponse> {
    try {
      const response = await this.api.post<IAuthResponse>(LOGIN_URL, credentials);
      const { access, refresh, session } = response.data;

      this.setToken(access);
      this.setRefreshToken(refresh);
      this.setSession(session);
      this.setRedirectPrefix("/device/");

      return response.data as IAuthResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Login failed: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // Optional: Call backend to invalidate token
      // await this.api.post('/auth/logout');
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(REDIRECT_ROUTE_PREFIX_KEY);
      localStorage.removeItem(DEVICE_TAG_KEY);
      localStorage.removeItem(EXTENSIONS_DEVICE_TAG_KEY);
      localStorage.removeItem(EXTENSIONS_ACCESS_TOKEN_KEY);

      // Clear the Customer.io identity so it never carries into the next visitor
      // on a shared device. Dynamic import avoids a static import cycle
      // (customerio imports authHelper); reset is best-effort.
      try {
        const { resetCustomerio } = await import("@/customerio");
        resetCustomerio();
      } catch {
        // Ignore — identity reset must never block logout.
      }
    }
  }

  /**
   * Returns the device tag the backend has authenticated this browser for, or
   * `null` when the browser is not authenticated.
   *
   * Resolves from the session (old `/device/` login flow) then the stored
   * device tag (SuperPacman flow) — mirroring `logoutAndRedirect`. Used to gate
   * Customer.io identification so it can never be driven by an unauthenticated
   * URL device tag.
   */
  getAuthenticatedDeviceTag(): string | null {
    if (!this.isAuthenticated()) return null;
    return this.getSession()?.deviceTag ?? this.getDeviceTag();
  }

  /**
   * Clears all auth state and redirects the user back to their device URL so
   * the app can re-authenticate from scratch.
   *
   * Device tag resolution order:
   *  1. `session_data` (set by the old `/device/` login flow via `setSession`)
   *  2. `device_tag` localStorage key (set by the SuperPacman installment fetch)
   *
   * If no device tag can be resolved the redirect is skipped; the caller is
   * responsible for surfacing an error to the user in that case.
   */
  private async logoutAndRedirect(): Promise<void> {
    const deviceTag = this.getSession()?.deviceTag ?? this.getDeviceTag();
    const prefix = this.getRedirectPrefix() ?? "/device/";
    await this.logout();
    if (deviceTag) {
      window.location.href = `${prefix}${deviceTag}`;
    }
  }

  /**
   * Clears only the Extensions-service auth state and redirects back to the
   * extensions URL for the current device, without touching the main
   * `/device/` session. Used by the extensions API interceptor so a failed
   * extensions token refresh never logs out an unrelated, healthy main
   * session sharing the same browser.
   */
  private logoutExtensionsAndRedirect(): void {
    const deviceTag = this.getExtensionsDeviceTag();
    localStorage.removeItem(EXTENSIONS_ACCESS_TOKEN_KEY);
    localStorage.removeItem(EXTENSIONS_DEVICE_TAG_KEY);
    if (deviceTag) {
      window.location.href = `/extensions/device/${deviceTag}`;
    }
  }

  private setToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  private setExtensionsToken(token: string): void {
    localStorage.setItem(EXTENSIONS_ACCESS_TOKEN_KEY, token);
  }

  private getExtensionsToken(): string | null {
    return localStorage.getItem(EXTENSIONS_ACCESS_TOKEN_KEY);
  }

  setExtensionsDeviceTag(deviceTag: string): void {
    localStorage.setItem(EXTENSIONS_DEVICE_TAG_KEY, deviceTag);
  }

  private getExtensionsDeviceTag(): string | null {
    return localStorage.getItem(EXTENSIONS_DEVICE_TAG_KEY);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private setSession(session: ISession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  getSession(): ISession | null {
    const session = localStorage.getItem(SESSION_KEY);
    return session ? JSON.parse(session) : null;
  }

  private setDeviceTag(deviceTag: string): void {
    localStorage.setItem(DEVICE_TAG_KEY, deviceTag);
  }

  private getDeviceTag(): string | null {
    return localStorage.getItem(DEVICE_TAG_KEY);
  }

  private setRedirectPrefix(routePrefix: string): void {
    localStorage.setItem(REDIRECT_ROUTE_PREFIX_KEY, routePrefix);
  }

  private getRedirectPrefix(): string | null {
    return localStorage.getItem(REDIRECT_ROUTE_PREFIX_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isExtensionsAuthenticated(): boolean {
    return !!this.getExtensionsToken();
  }

  // Helper method to get installment information
  getInstallments(): IInstallmentDetail[] | null {
    const session = this.getSession();
    return session?.installment.installments ?? null;
  }

  // Helper method to get current currency
  getCurrency(): { currency: string; symbol: string } | null {
    const session = this.getSession();
    if (!session) return null;
    return {
      currency: session.installment.currency,
      symbol: session.installment.currencySymbol,
    };
  }

  async getProviders(
    params: { amount?: number | null; lang?: string } = {},
  ): Promise<{ providers: IPaymentProviderBackend[]; globalWarning: IGlobalWarning | null }> {
    const maxAttempts = 3;
    let attempts = 0;
    const queryParams: Record<string, string | number> = {};
    if (params.amount != null) queryParams.amount = params.amount;
    if (params.lang) queryParams.lang = params.lang;

    while (attempts < maxAttempts) {
      try {
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }

        const response = await this.api.get<IProvidersResponse>(PROVIDERS_URL, { params: queryParams });
        return {
          providers: response.data.results as IPaymentProviderBackend[],
          globalWarning: response.data.global_warning ?? null,
        };
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch providers after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch providers after multiple attempts", 500);
  }

  async getUIFlags(): Promise<Record<string, boolean>> {
    try {
      if (!this.isAuthenticated()) return {};
      const response = await this.api.get<{ flags: Record<string, boolean> }>(UI_FLAGS_URL);
      return response.data.flags ?? {};
    } catch {
      return {};
    }
  }

  async getReference(params: IReferenceRequest): Promise<IReference> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }
        const response = await this.api.post<IReferenceResponse>(REFERENCE_URL, params);
        const responseData = snakeToCamel<IReferenceResponse>(response.data);
        if (!responseData.reference && responseData.errorDetails) {
          throw new IAuthError(responseData.errorDetails.messages.toString(), 400, responseData.errorDetails);
        }
        const validThrough = responseData.reference.expirationTime as unknown as number;
        if (validThrough) {
          responseData.reference.expirationTime = formatDate(validThrough);
        } else {
          responseData.reference.expirationTime = "";
        }

        return responseData.reference;
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          const responseStatus = error.response?.status;
          // A 4xx is a deterministic rejection (e.g. amount out of range) — retrying
          // the same request won't change the outcome, so fail fast and carry the
          // backend's error body through instead of masking it behind 3 retries.
          if (responseStatus !== undefined && responseStatus >= 400 && responseStatus < 500) {
            throw new IAuthError(error.message, responseStatus, error.response?.data);
          }
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch reference after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
        } else if (error instanceof IAuthError) {
          if (params.paymentType === Providers.CODI && error.statusCode === 400) {
            return {
              referenceCode: "",
              deviceTag: params.deviceTag,
              countryCode: params.countryCode || "",
              paymentType: params.paymentType,
              errorDetails: error.response,
            };
          }
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch reference after multiple attempts", 500);
  }

  /**
   * Fetches a virtual account number for the user.
   */
  async getVirtualAccountNumber(
    params: ISuperPacmanVirtualAccountNumberRequest,
  ): Promise<ISuperPacmanVirtualAccountNumberResponse> {
    const args = new URLSearchParams({
      sharedSecret: SHARED_PASSWORD,
      financeProductType: params.financeProductType,
      financeProductId: params.financeProductId.toString(),
    });
    const response = await this.api.get(`super-pacman/virtual-account-number/?${args}`);
    return snakeToCamel<ISuperPacmanVirtualAccountNumberResponse>(response.data);
  }

  /**
   * Fetches the pre-signed acceptance token for Wompi.
   */
  async getWompiPreSignedAcceptanceToken(): Promise<IWompiAcceptanceTokenResponse> {
    const response = await this.api.get<IWompiAcceptanceTokenResponse>(
      `super-pacman/wompi-acceptance-token/?sharedSecret=${SHARED_PASSWORD}`,
    );
    return snakeToCamel<IWompiAcceptanceTokenResponse>(response.data);
  }

  async logAction(payload: IActionLogsRequest, gTag: VueGtag | null = null): Promise<IActionLogsResponse> {
    // TODO: VPOP-234 Add env validation inside, and an optional bypass argument to force logs.
    try {
      if (!this.isAuthenticated()) throw new IAuthError("User not authenticated", 401);

      // The action-log serializer defaults these optional fields to "Unreported"
      // when absent/blank, but rejects an explicit null. Callers often read them
      // from the store, which is null during transitions (e.g. right after an
      // external redirect back, before installments load), so drop null/undefined
      // values here rather than 400 the log call.
      const body: IActionLogsRequest = { ...payload };
      (["country", "productType"] as const).forEach((key) => {
        if (body[key] == null) delete body[key];
      });

      const response = await this.api.post(LOG_URL, body);
      if (gTag) {
        let data;
        if (payload.data) {
          data = payload.data;
        } else {
          data = payload;
        }
        logGTagEvent(gTag, payload.action, {
          event_category: payload.actionType,
          event_label: payload.action,
          value: data,
        });
      }
      return response.data as IActionLogsResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Failed to log action: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  async fetchSuperPacmanInstallments(request: ISuperPacmanFetchInstallmentRequest): Promise<ISuperPacmanInstallment> {
    try {
      const response = await this.api.post("super-pacman/installments/", request);
      const { access, refresh } = response.data;

      this.setToken(access);
      this.setRefreshToken(refresh);
      this.setRedirectPrefix("/p/device/");
      this.setDeviceTag(request.deviceTag);

      return response.data.data as ISuperPacmanInstallment;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError("Login failed: " + error.message, (error as AxiosError).response?.status);
      }
      throw error;
    }
  }

  /**
   * Fetches the reference from Super Pacman.
   */
  async fetchSuperPacmanReference<T = ISuperPacmanReferenceResponse>(
    request: ISuperPacmanReferenceRequest,
  ): Promise<T> {
    try {
      request["sharedSecret"] = SHARED_PASSWORD;
      const response = await this.api.post("super-pacman/reference/", request);
      if (response?.data) {
        return snakeToCamel<T>(response.data);
      } else {
        throw new Error("Empty response data");
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a Xendit payment request for the specified channel.
   * Proxies to the xendit_integration service via the middleware.
   */
  async fetchXenditReference(request: IXenditReferenceRequest): Promise<IXenditReferenceResponse> {
    const payload = { ...request, sharedSecret: SHARED_PASSWORD };
    const response = await this.api.post("xendit-reference/", payload);
    if (response?.data) {
      return snakeToCamel<IXenditReferenceResponse>(response.data);
    }
    throw new Error("Empty response data");
  }

  /**
   * Fetches the PSE financial institutions.
   */
  async fetchPSEFinancialInstitutions(): Promise<{ label: string; value: string }[]> {
    try {
      const response = await this.api.get("super-pacman/pse-financial-institutions/");
      return response.data.map((institution: Record<string, string>) => ({
        label: institution["financial_institution_name"],
        value: institution["financial_institution_code"],
      }));
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError(
          "Failed to fetch PSE financial institutions: " + error.message,
          (error as AxiosError).response?.status,
        );
      }
      throw error;
    }
  }

  /**
   * Fetches the transaction details from Wompi.
   */
  async getWompiTransaction(transactionId: string): Promise<IWompiTransactionResponse> {
    const response = await this.api.get(`super-pacman/wompi-get-transaction/${transactionId}/`);
    return snakeToCamel<IWompiTransactionResponse>(response.data);
  }

  /**
   * Gets or creates a Bitso CLABE reference for the user.
   * This calls the middleware which proxies to bitso_integration service.
   */
  async getBitsoReference(params: IBitsoReferenceRequest): Promise<IReference> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        // Call middleware endpoint (NOT bitso_integration directly)
        // Send sharedSecret as header to ensure it's available during authentication
        if (!this.isAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }
        const response = await this.api.post<IBitsoReferenceResponse>(
          "super-pacman/bitso-reference/",
          {
            userId: params.userId,
            customerNationalIdNumber: params.customerNationalIdNumber,
            customerNationalIdType: params.customerNationalIdType,
            customerNationalIdCountry: params.customerNationalIdCountry,
          },
          {
            headers: {
              "X-Sharedsecret": SHARED_PASSWORD,
            },
          },
        );

        const responseData = snakeToCamel<IBitsoReferenceResponse>(response.data);

        return {
          referenceCode: responseData.clabe || "",
          deviceTag: params.deviceTag,
          countryCode: params.countryCode || "",
          paymentType: Providers.BITSO,
        };
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch Bitso reference after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * (attempts + 1)));
        } else {
          throw error;
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch Bitso reference after multiple attempts", 500);
  }

  async initiateExtensionRequest(params: IInitiateExtensionRequest): Promise<IInitiateExtensionResponse> {
    try {
      // enforce a new token for each initiate request
      localStorage.removeItem(EXTENSIONS_ACCESS_TOKEN_KEY);
      const response = await this.extensionsApi.post<IInitiateExtensionResponse>("", params);
      return response.data;

    } catch (error) {
      // The extensionsApi response interceptor already wraps failures as
      // IAuthError, carrying the response body in `.response` — preserve it so
      // callers can inspect error_code (e.g. SERVICE_UNAVAILABLE).
      if (error instanceof IAuthError) {
        throw error;
      }
      if (error instanceof Error) {
          throw new IAuthError(
            "Failed to initiate extension request: " + error.message,
            (error as AxiosError).response?.status,
            (error as AxiosError).response?.data,
          );
      }
    }
    throw new IAuthError("Failed to initiate extension request", 500);
  }

  async getAvailableExtensionOptions(requestId: string): Promise<{ extensionOptions: IExtensionOption[] }> {
    const maxAttempts = 3;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        if (!this.isExtensionsAuthenticated()) {
          throw new IAuthError("User not authenticated", 401);
        }

        const url = `${requestId}/options/`;
        const response = await this.extensionsApi.get<IAvailableExtensionsResponse>(url);
        return {
          extensionOptions: response.data.options.map((option: IExtensionOptionBackend): IExtensionOption => {
            return {
              extensionType: option.extensionType,
              extensionTime: option.minTime,
              title: String(option.minTime),
              subtitle: option.extensionType,
              status: option.status,
              reasonsKey: option.reasonsKey,
              uiReasonKeys: option.uiReasonKeys,
              uiReasons: option.uiReasons,
              extensionData: option.extensionData
            } as IExtensionOption;
          }),
        };
      } catch (error) {
        if (error instanceof Error) {
          console.error(`Attempt ${attempts + 1} failed: ${error.message}`);
          if (attempts === maxAttempts - 1) {
            throw new IAuthError(
              "Failed to fetch available extensions after multiple attempts: " + error.message,
              (error as AxiosError).response?.status,
            );
          }
        }
      }
      attempts++;
    }
    throw new IAuthError("Failed to fetch available extensions after multiple attempts", 500);
  }

  async grantExtension(requestId: string, params: ISelectExtensionRequest): Promise<ISelectExtensionResponse> {
    try {
      if (!this.isExtensionsAuthenticated()) {
        throw new IAuthError("User not authenticated", 401);
      }

      const url = `${requestId}/select/`;
      const response = await this.extensionsApi.post<ISelectExtensionResponse>(url, params);
      return response.data;

    } catch (error) {
      if (error instanceof IAuthError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new IAuthError(
          "Failed to grant extension: " + error.message,
          (error as AxiosError).response?.status,
        );
      }
    }
    throw new IAuthError("Failed to grant extension", 500);
  }

  /**
   * Checks which devices fall inside a rollout experiment.
   *
   * @param request - Rollout name and list of just one device ID to evaluate.
   * @returns Active status of the rollout and per-device assignment results.
   */
  async getRolloutDeviceAssignment(
    request: IRolloutDeviceAssignmentRequest,
  ): Promise<IRolloutDeviceAssignmentResponse> {
    try {
      const response = await this.api.post<IRolloutDeviceAssignmentResponse>(
        ROLLOUT_DEVICE_ASSIGNMENT_URL,
        { ...request, sharedSecret: SHARED_PASSWORD },
      );
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        throw new IAuthError(
          "Failed to fetch rollout device assignment: " + error.message,
          (error as AxiosError).response?.status,
        );
      }
      throw new IAuthError("Failed to fetch rollout device assignment", 500);
    }
  }
}

// Create singleton instance
const authHelper = new AuthHelper();
export default authHelper;
